import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  getStripeStatus,
  createStripeOnboardingLink,
  refreshStripeStatus,
} from '../services/internApi';

const PORTAL_BASE = (typeof window !== 'undefined' ? window.location.origin : 'https://interns.cyberwisecorp.com');
const RETURN_URL = `${PORTAL_BASE}/payouts/setup?stripe=return`;
const REFRESH_URL = `${PORTAL_BASE}/payouts/setup?stripe=refresh`;

function HelpRow({ label, value }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75 }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>{value}</Typography>
    </Box>
  );
}

export default function StripeOnboardingPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const stripeFlag = searchParams.get('stripe');

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async (forceRefresh = false) => {
    try {
      const fn = forceRefresh ? refreshStripeStatus : getStripeStatus;
      const data = await fn();
      setStatus(data);
      setError('');
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to load Stripe status');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // On mount: read current status. If returning from Stripe, force a refresh.
  useEffect(() => {
    const force = stripeFlag === 'return' || stripeFlag === 'refresh';
    if (force) {
      setRefreshing(true);
      load(true).then(() => {
        // Clean the URL once we've refreshed.
        const next = new URLSearchParams(searchParams);
        next.delete('stripe');
        setSearchParams(next, { replace: true });
      });
    } else {
      load(false);
    }
    // Run once on mount; load/searchParams stay stable enough that the lint
    // exhaustive-deps rule would just churn — disable for this hook.
    // eslint-disable-next-line
  }, []);

  const handleStartOnboarding = async () => {
    setCreating(true);
    setError('');
    try {
      const { url } = await createStripeOnboardingLink(RETURN_URL, REFRESH_URL);
      if (url) {
        window.location.href = url;
      } else {
        setError('Stripe did not return an onboarding URL. Please try again.');
      }
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to start onboarding');
    } finally {
      setCreating(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await load(true);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const hasAccount = !!status?.has_account;
  const payoutsEnabled = !!status?.stripe_payouts_enabled;
  const detailsSubmitted = !!status?.stripe_details_submitted;
  const requirementsDue = Array.isArray(status?.stripe_requirements_due) ? status.stripe_requirements_due : [];
  const disabledReason = status?.stripe_disabled_reason || '';
  const graceRemaining = status?.stripe_grace_days_remaining;

  return (
    <Box sx={{ maxWidth: 760 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <AccountBalanceWalletIcon sx={{ fontSize: 32, color: 'primary.main' }} />
        <Box>
          <Typography variant="h4" sx={{ mb: 0.5 }}>Payment Setup</Typography>
          <Typography variant="body1" color="text.secondary">
            Connect a Stripe account so CWC can pay you for completed assessments.
          </Typography>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Top-line status banner */}
      {payoutsEnabled ? (
        <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 3 }}>
          <AlertTitle>You're all set</AlertTitle>
          Stripe payouts are enabled. Your assessment earnings will deposit directly to your linked bank account
          on the standard 2-day schedule. You can also accept jobs and earn referral bonuses.
        </Alert>
      ) : hasAccount && requirementsDue.length > 0 ? (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <AlertTitle>Stripe needs more information</AlertTitle>
          Click "Continue setup" below to finish providing the requested details.
        </Alert>
      ) : hasAccount ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          <AlertTitle>Almost done</AlertTitle>
          Your Stripe account exists but isn't enabled for payouts yet. Continue setup to finish onboarding.
        </Alert>
      ) : (
        <Alert severity="info" sx={{ mb: 3 }}>
          <AlertTitle>Get paid for your work</AlertTitle>
          Stripe is our payout partner. You'll provide identity and banking information directly to Stripe — CWC
          never sees your bank details. Payouts arrive on a standard 2-day schedule.
        </Alert>
      )}

      <Paper sx={{ p: { xs: 2.5, sm: 3.5 }, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Account Status</Typography>

        <Stack spacing={1.5} divider={<Divider flexItem />}>
          <HelpRow
            label="Stripe Connect account"
            value={hasAccount ? <Chip size="small" label="Created" color="success" /> : <Chip size="small" label="Not created" />}
          />
          <HelpRow
            label="Identity details submitted"
            value={detailsSubmitted ? <Chip size="small" label="Submitted" color="success" /> : <Chip size="small" label="Incomplete" color="default" />}
          />
          <HelpRow
            label="Payouts enabled"
            value={payoutsEnabled ? <Chip size="small" label="Enabled" color="success" /> : <Chip size="small" label="Pending" color="warning" />}
          />
          {!payoutsEnabled && graceRemaining != null && (
            <HelpRow
              label="Grace period remaining"
              value={
                <Chip
                  size="small"
                  label={graceRemaining > 0 ? `${graceRemaining} day${graceRemaining === 1 ? '' : 's'}` : 'Expired'}
                  color={graceRemaining > 0 ? 'warning' : 'error'}
                />
              }
            />
          )}
        </Stack>

        {requirementsDue.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Stripe is asking for:</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {requirementsDue.map((r) => (
                <Chip key={r} size="small" label={r} variant="outlined" />
              ))}
            </Box>
          </Box>
        )}

        {disabledReason && (
          <Alert severity="warning" sx={{ mt: 3 }}>
            <strong>Stripe note:</strong> {disabledReason}
          </Alert>
        )}

        <Box sx={{ mt: 3, display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
          {!payoutsEnabled && (
            <Button
              variant="contained"
              size="large"
              startIcon={creating ? <CircularProgress size={18} color="inherit" /> : <OpenInNewIcon />}
              onClick={handleStartOnboarding}
              disabled={creating}
              sx={{ background: 'linear-gradient(135deg, #1e3a8a, #7c3aed)' }}
            >
              {hasAccount ? 'Continue setup with Stripe' : 'Set up payouts with Stripe'}
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={refreshing ? <CircularProgress size={18} /> : <RefreshIcon />}
            onClick={handleRefresh}
            disabled={refreshing}
          >
            Refresh status
          </Button>
          {payoutsEnabled && (
            <Button variant="text" onClick={() => navigate('/')}>
              Back to Dashboard
            </Button>
          )}
        </Box>
      </Paper>

      <Paper sx={{ p: { xs: 2.5, sm: 3.5 } }}>
        <Typography variant="h6" sx={{ mb: 1.5 }}>How payouts work</Typography>
        <Stack spacing={1.25}>
          <Typography variant="body2" color="text.secondary">
            • You earn $140 per Home Assessment and $240 per Business Assessment. Pay is fixed and
            transferred to your Stripe account after a mentor approves your report.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            • Stripe deposits funds to your bank account on a standard 2-day rolling schedule.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            • CWC absorbs Stripe processing fees. You keep the full payout amount.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            • Referral bonuses ($10 per new client booked with your code) are paid the same way.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            • You'll receive a 1099 form from Stripe if you earn $600+ in a calendar year.
          </Typography>
        </Stack>
      </Paper>
    </Box>
  );
}
